import React, { useState, useRef, useEffect, forwardRef, useMemo, useCallback, useLayoutEffect } from 'react';

// Create a stable comparison function outside the component
const areEventsEqual = (prevEvents, nextEvents) => {
  // Handle null cases - if prevEvents is null (initial state), they're never equal
  if (prevEvents === null) {
    return false;
  }
  
  // Handle other null/undefined cases
  if (!prevEvents && !nextEvents) return true;
  if (!prevEvents || !nextEvents) return false;
  
  // Handle non-array cases
  if (!Array.isArray(prevEvents) || !Array.isArray(nextEvents)) return false;
  
  // Handle length mismatch
  if (prevEvents.length !== nextEvents.length) {
    return false;
  }
  
  // Compare by id and date which are the key properties we care about
  return prevEvents.every((prev, i) => {
    const next = nextEvents[i];
    return prev?.id === next?.id && prev?.event_date === next?.event_date;
  });
};

// Create a props comparison function for React.memo
const propsAreEqual = (prevProps, nextProps) => {
  return prevProps.loading === nextProps.loading && areEventsEqual(prevProps.events, nextProps.events);
};

// Memoize the entire component to prevent unnecessary re-renders from parent
const EventListComponent = forwardRef(({ events, loading = false }, ref) => {
  
  const [state, setState] = useState({
    visibleIndexes: [],
    isDataChanging: false,
    localEvents: []
  });
  const { visibleIndexes, isDataChanging, localEvents } = state;

  const containerRef = useRef(null);
  const isInitialMount = useRef(true);
  const animationTimersRef = useRef([]);
  const previousEventsRef = useRef(null); // Initialize with null to ensure first change is detected

  // Memoize sorted events
  const sortedEvents = useMemo(() => {
    if (!events || !Array.isArray(events)) return [];
    return [...events].sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
  }, [events]);

    // Batch state updates together
  const updateState = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      ...updates
    }));
   }, []);

  // Memoize the animation function
  const startFadeInAnimation = useCallback((eventsToAnimate) => {
    // Clear any existing animation timers
    animationTimersRef.current.forEach(timer => clearTimeout(timer));
    animationTimersRef.current = [];

    // Batch our state updates
    updateState({
      visibleIndexes: [],
      isDataChanging: true
    });
    
    const timer = setTimeout(() => {
      updateState({ isDataChanging: false });
      const initialDelay = 90;
      
      eventsToAnimate.forEach((_, index) => {
        const fadeTimer = setTimeout(() => {
          setState(prev => ({
            ...prev,
            visibleIndexes: [...prev.visibleIndexes, index]
          }));
        }, initialDelay + index * 100);
        animationTimersRef.current.push(fadeTimer);
      });
    }, 50);
    
    animationTimersRef.current.push(timer);
  }, [updateState]);

  // Use useLayoutEffect to handle animations to prevent flicker
  useLayoutEffect(() => {
    // Skip if events haven't actually changed
    if (areEventsEqual(events, previousEventsRef.current)) {
      // But if we have events and no visible indexes, we might need to restart animation
      if (events && events.length > 0 && visibleIndexes.length === 0 && localEvents.length > 0 && !isDataChanging) {
        // Same events but no visible indexes, restarting animation
        startFadeInAnimation(localEvents);
      }
      
      return;
    }
    
    previousEventsRef.current = events ? [...events] : []; // Store a copy, not the same reference

    if (isInitialMount.current) {
      updateState({ localEvents: sortedEvents });
      if (sortedEvents.length > 0) {
        requestAnimationFrame(() => startFadeInAnimation(sortedEvents));
      } else {
        updateState({ isDataChanging: false });
      }
      isInitialMount.current = false;
    } else {
      
      // Only update if the events have actually changed
      if (!areEventsEqual(sortedEvents, localEvents)) {
        updateState({
          isDataChanging: true,
          visibleIndexes: []
        });

        const clearDelay = localEvents.length > 0 ? 50 : 0;
        const clearTimer = setTimeout(() => {
          if (localEvents.length > 0) {
            updateState({ localEvents: [] });
          }

          const setNewEventsTimer = setTimeout(() => {
            updateState({ localEvents: sortedEvents });
            if (sortedEvents.length > 0) {
              startFadeInAnimation(sortedEvents);
            } else {
              const finalTimer = setTimeout(() => updateState({ isDataChanging: false }), 50);
              animationTimersRef.current.push(finalTimer);
            }
          }, localEvents.length > 0 ? 50 : 0);

          animationTimersRef.current.push(setNewEventsTimer);
        }, clearDelay);

        animationTimersRef.current.push(clearTimer);
      }
    }
  }, [events, sortedEvents, localEvents, startFadeInAnimation, updateState]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      animationTimersRef.current.forEach(timer => clearTimeout(timer));
      animationTimersRef.current = [];
    };
  }, []);

  // Track when localEvents changes
  useEffect(() => {
    // localEvents updated
  }, [localEvents]);

  // Memoize the formatDate function
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return 'No date';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return 'Invalid date';
    return dateObj.toLocaleDateString(undefined, options);
  }, []);

  // Memoize style functions
  const getSeverityStyle = useCallback((severity) => {
    const styles = {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.25rem 0.5rem',
      fontSize: 'var(--font-size-sm)',
      fontWeight: '600',
      color: 'var(--color-background-paper)',
      borderRadius: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    };

    switch (severity?.toLowerCase()) {
      case 'high':
        return { ...styles, backgroundColor: 'var(--color-severity-high)' };
      case 'medium':
        return { ...styles, backgroundColor: 'var(--color-severity-medium)' };
      case 'low':
        return { ...styles, backgroundColor: 'var(--color-severity-low)' };
      default:
        return { ...styles, backgroundColor: 'var(--color-severity-none)' };
    }
  }, []);

  const getSourceStyle = useCallback(() => {
    return {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.25rem 0.5rem',
      fontSize: 'var(--font-size-sm)',
      fontWeight: '600',
      color: 'var(--color-text-secondary)',
      backgroundColor: 'var(--color-background)',
      borderRadius: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid var(--color-divider)'
    };
  }, []);

  return (
    <div 
      className={`event-list ${isDataChanging ? 'changing' : ''}`}
      ref={(node) => {
        containerRef.current = node;
        if (ref) {
          ref.current = node;
        }
      }}
      style={{ 
        padding: '1rem',
        backgroundColor: 'var(--color-background-paper)',
        borderRadius: '4px',
        boxShadow: '0 1px 3px var(--color-action-disabled-background)'
      }}
    >
      {localEvents?.map((event, index) => (
        <div
          key={event.id || index}
          style={{
            opacity: visibleIndexes.includes(index) ? 1 : 0,
            transform: `translateY(${visibleIndexes.includes(index) ? 0 : 20}px)`,
            transition: 'all 0.3s ease',
            padding: '0.1rem',
            paddingBottom: '1rem',
            marginBottom: '0.5rem',
            borderBottom: '1px solid var(--color-text-disabled)',
            backgroundColor: 'var(--color-background-paper)'
          }}
        >
          <div style={{ marginBottom: '0.5rem', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-md)' }}>
            {formatDate(event.event_date)}
          </div>
          <div style={{ 
            marginBottom: '0.5rem',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-lg)',
            fontWeight: '600',
            lineHeight: 'var(--line-height-normal)',
            fontFamily: 'var(--font-family-heading) !important'
          }}>
            {event.summary_title}
          </div>
          <div style={{ 
            marginBottom: '0.5rem',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-md)',
            lineHeight: 'var(--line-height-normal)'
          }}>
            {event.description}
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {event.severity && (
              <span style={getSeverityStyle(event.severity)}>
                {event.severity}
              </span>
            )}
            {event.url && (
              <a 
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...getSourceStyle(),
                  textDecoration: 'none',
                  gap: '0.25rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = 'var(--color-background-hover)';
                  e.target.style.borderColor = 'var(--color-primary)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'var(--color-background)';
                  e.target.style.borderColor = 'var(--color-divider)';
                }}
              >
                {event.source || 'Source'} 
                <span style={{ fontSize: 'var(--font-size-md)', opacity: 0.7 }}>↗</span>
              </a>
            )}
          </div>
        </div>
      ))}
      {(!localEvents || localEvents.length === 0) && !isDataChanging && !loading && (!events || events.length === 0) && (
        <div style={{ 
          textAlign: 'center',
          padding: '2rem',
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-md)'
        }}>
          No events found
        </div>
      )}
    </div>
  );
});

EventListComponent.displayName = 'EventListComponent';

export default React.memo(EventListComponent, propsAreEqual);
